import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Plus, X, Image as ImageIcon } from 'lucide-react';
import Image from '../../../components/AppImage';
import { cn } from '../../../utils/cn';

const sections = [
  {
    id: 'standard-stainless',
    title: 'Standard Stainless Cabin Decoration',
    folder: '1-standard-stainless-cabin-decoration',
    description: 'Durable and elegant stainless steel finishes for modern elevator interiors.',
    images: ['5.JPG', '6.JPG', '7.JPG', '8.JPG', '9.JPG', '10.JPG']
  },
  {
    id: 'optional-glowing',
    title: 'Optional Glowing Acrylic Integrated Cabin',
    folder: '2-optional-glowing-acrylic-integrated-cabin',
    description: 'Sophisticated lighting integrated with acrylic panels for a premium aesthetic.',
    images: ['11.JPG', '12.JPG', '13.JPG', '14.JPG', '15.JPG', '16.JPG']
  },
  {
    id: 'ceiling-series',
    title: 'Ceiling Series',
    folder: '3-ceiling-series',
    description: 'A wide range of architectural ceiling designs with integrated LED lighting.',
    images: ['17.JPG', '18.JPG', '19.JPG', '20.JPG', '21.JPG', '22.JPG']
  },
  {
    id: 'standard-floor',
    title: 'Standard Floor PVC',
    folder: '4-standard-floor-pvc',
    description: 'High-traffic PVC flooring options with various patterns and textures.',
    images: ['23.JPG', '24.JPG']
  },
  {
    id: 'lop-cop-1',
    title: 'LOP & COP Series 1',
    folder: '5-lop&cop',
    description: 'Modern Landing Operating Panels and Car Operating Panels for intuitive control.',
    images: ['25.JPG', '26.JPG', '27.JPG', '28.JPG', '29.JPG', '30.JPG', '31.JPG', '32.JPG']
  },
  {
    id: 'color-options-1',
    title: 'General Color Options',
    folder: '6-color-options',
    description: 'Extensive palette of colors and finishes to match any interior design.',
    images: ['33.JPG', '34.JPG', '35.JPG', '36.JPG']
  },
  {
    id: 'n800-decoration',
    title: 'N800 Decoration',
    folder: '7-N800 decoration',
    description: 'Specific decorative elements designed for the N800 series elevators.',
    images: ['37.JPG', '38.JPG']
  },
  {
    id: 'k900-color',
    title: 'K900 Color Options',
    folder: '8-K900 color options',
    description: 'Exclusive color palettes for our premium K900 elevator series.',
    images: ['39.JPG', '40.JPG']
  },
  {
    id: 'k600-decoration',
    title: 'K600 Cabin Decoration',
    folder: '9-K600 cabin-decoration',
    description: 'Artistic and functional cabin designs for K600 series elevators.',
    images: ['41.JPG', '42.JPG', '43.JPG', '44.JPG']
  },
  {
    id: 'landing-door',
    title: 'Landing Door Series',
    folder: '91-landing-door',
    description: 'High-quality landing doors with various finishes and glass options.',
    images: ['45.JPG', '46.JPG', '47.JPG', '48.JPG']
  },
  {
    id: 'lop-cop-2',
    title: 'LOP & COP Series 2',
    folder: '92-lop & cop',
    description: 'Additional designs for operating panels focusing on ergonomics and style.',
    images: ['49.JPG', '50.JPG', '51.JPG', '52.JPG']
  },
  {
    id: 'k900-panoramic',
    title: 'K900 Panoramic Villa Elevator',
    folder: '93-K900 panormic villa elevator',
    description: 'Independent frame glass design, without additional construction costs. With good transparency, integrate with interior decoration.',
    specifications: [
      { label: 'Load (Kg)', value: '400' },
      { label: 'Min Pit (mm)', value: '0' },
      { label: 'Speed (m/s)', value: '0.4' },
      { label: 'Min OH (mm)', value: '2750' }
    ],
    configuration: [
      'Monadrive/Matodrive',
      'Touch screen COP&LOP',
      '5+5 tempered glass cabin and door',
      'Aluminum alloy glass shaft, 8 color options',
      'Hpmont V2A/Monarch 3000 controller',
      'LED light strip',
      'Ouling door Operator',
      'German ELGO magnetic scale-absolute position shaft measurement system',
      'Huning electronic safety gear'
    ],
    images: ['1.JPG']
  },
  {
    id: 'n800-traction',
    title: 'N800 Traction Platform Lift',
    folder: 'N800 tranction platform lift',
    description: 'N800 series is the most high-end elevator type for now. The integrated shaft is made of aviation aluminum alloy, high strength 5+5 laminated glass is also part of the shaft support, so the shaft can be extremely narrow. The powder coating of aluminum alloy is also the most high-end choice, with good pearl luster and luxury appearance.',
    specifications: [
      { label: 'Max Load (Kg)', value: '400' },
      { label: 'Min Pit (mm)', value: '0' },
      { label: 'Speed (m/s)', value: '0.3' },
      { label: 'Max Floor', value: '6' },
      { label: 'Min OH (mm)', value: '2300' },
      { label: 'Min install space (mm)', value: '800*900' }
    ],
    configuration: [
      'AMNEO steel belt traction machine',
      'Hpmont V2A controller',
      'Touchscreen COP&LOP',
      'Soft LED light strip',
      'Aluminum alloy handrail',
      'Customize pattern lighted acrylic car board',
      'Aviation aluminum backpack rack',
      'German ELGO magnetic scale-absolute position shaft measurement system'
    ],
    images: ['2.JPG']
  }
];

const ExpandableSolutions = () => {
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <section className="py-24 bg-[#F8F6F4]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex items-center space-x-3 text-[#91756A] mb-4"
          >
            <div className="w-12 h-[2px] bg-[#91756A]"></div>
            <span className="text-xs font-bold uppercase tracking-[0.4em]">Design Options</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-[#4D4C4A] italic"
          >
            CUSTOMIZATION <span className="text-[#91756A]">&</span> FINISHES
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mt-6 text-[#75716C] max-w-2xl text-lg"
          >
            Explore our extensive range of interior designs, materials, and technological components. Click on a category to view detailed options and images.
          </motion.p>
        </div>

        {/* Card Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="group cursor-pointer"
              onClick={() => setSelectedSection(section)}
            >
              <div className="relative aspect-[4/3] overflow-hidden rounded-xl bg-gray-200 shadow-sm group-hover:shadow-xl transition-all duration-300">
                <Image
                  src={`/assets/images/expandable-sections/${section.folder}/${section.images[0]}`}
                  alt={section.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent opacity-60 group-hover:opacity-80 transition-opacity"></div>
                
                {/* Label Background */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent pt-12 pb-4 px-4">
                  <span className="text-[#91756A] text-[10px] font-bold uppercase tracking-widest block mb-1">
                    {index + 1} / {sections.length}
                  </span>
                  <h3 className="text-white text-lg font-semibold italic leading-tight">
                    {section.title}
                  </h3>
                </div>

                <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                  <ChevronRight className="text-white w-4 h-4" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Compact Scrollable Modal */}
      <AnimatePresence>
        {selectedSection && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            onClick={() => setSelectedSection(null)}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>

            {/* Modal Container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button 
                className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-[#91756A] transition-colors"
                onClick={() => setSelectedSection(null)}
              >
                <X size={20} />
              </button>

              {/* Scrollable Content */}
              <div className="overflow-y-auto max-h-[calc(90vh-80px)] scrollbar-thin scrollbar-thumb-[#91756A] scrollbar-track-gray-100">
                {/* Hero Image */}
                <div className="relative h-40 md:h-48 shrink-0">
                  <Image
                    src={`/assets/images/expandable-sections/${selectedSection.folder}/${selectedSection.images[0]}`}
                    alt={selectedSection.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#4D4C4A] via-[#4D4C4A]/40 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h2 className="text-xl md:text-2xl font-bold text-white italic tracking-tight">
                      {selectedSection.title}
                    </h2>
                  </div>
                </div>

                {/* Content */}
                <div className="px-6 py-8">
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-[#75716C] text-base leading-relaxed italic mb-8"
                >
                  {selectedSection.description}
                </motion.p>

                {/* Technical Specifications */}
                {selectedSection.specifications && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mb-8"
                  >
                    <h4 className="text-[#4D4C4A] font-bold uppercase tracking-widest text-xs mb-4 flex items-center">
                      <span className="w-6 h-px bg-[#91756A] mr-3"></span>
                      Technical Specifications
                    </h4>
                    <div className="overflow-hidden rounded-lg border border-[#75716C]/10 bg-white">
                      <table className="w-full text-left text-xs">
                        <tbody>
                          {selectedSection.specifications.map((spec, i) => (
                            <tr key={i} className="border-b border-[#75716C]/5 last:border-0">
                              <td className="py-2 px-4 text-[#75716C] font-medium">{spec.label}</td>
                              <td className="py-2 px-4 text-[#4D4C4A] font-bold">{spec.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </motion.div>
                )}

                {/* Standard Configuration */}
                {selectedSection.configuration && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="mb-8"
                  >
                    <h4 className="text-[#4D4C4A] font-bold uppercase tracking-widest text-xs mb-4 flex items-center">
                      <span className="w-6 h-px bg-[#91756A] mr-3"></span>
                      Standard Configuration
                    </h4>
                    <div className="grid grid-cols-1 gap-2 p-4 bg-white rounded-lg border border-[#75716C]/10">
                      {selectedSection.configuration.map((item, i) => (
                        <div key={i} className="flex items-start space-x-2">
                          <div className="mt-1 w-1 h-1 rounded-full bg-[#91756A]"></div>
                          <span className="text-[#4D4C4A] text-xs">{item}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Image Gallery */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mt-8"
                >
                  <h4 className="text-[#4D4C4A] font-bold uppercase tracking-widest text-xs mb-4 flex items-center">
                    <span className="w-6 h-px bg-[#91756A] mr-3"></span>
                    Gallery ({selectedSection.images.length})
                  </h4>
                  <div className={cn(
                    "grid gap-3",
                    selectedSection.images.length === 1 ? "grid-cols-1" :
                    selectedSection.images.length === 2 ? "grid-cols-2" :
                    "grid-cols-3 md:grid-cols-4"
                  )}>
                    {selectedSection.images.map((img, imgIdx) => (
                      <div
                        key={imgIdx}
                        className="relative aspect-square overflow-hidden rounded-lg bg-gray-100 cursor-pointer group"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImage({
                            src: `/assets/images/expandable-sections/${selectedSection.folder}/${img}`,
                            title: selectedSection.title,
                            index: imgIdx + 1,
                            total: selectedSection.images.length
                          });
                        }}
                      >
                        <Image
                          src={`/assets/images/expandable-sections/${selectedSection.folder}/${img}`}
                          alt={`${selectedSection.title} - ${imgIdx + 1}`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Plus className="text-white w-8 h-8" />
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
              </div>

              {/* Child Lightbox for Gallery Images */}
              <AnimatePresence>
                {selectedImage && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[200] overflow-auto bg-black/95"
                    onClick={() => setSelectedImage(null)}
                  >
                    <button 
                      className="fixed top-4 right-4 z-[210] w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-[#91756A] transition-colors"
                      onClick={() => setSelectedImage(null)}
                    >
                      <X size={20} />
                    </button>
                    <div 
                      className="min-h-screen flex items-center justify-center p-4 md:p-8"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        className="relative"
                      >
                        <Image
                          src={selectedImage.src}
                          alt={selectedImage.title}
                          className="max-w-full max-h-[90vh] object-contain"
                        />
                        <div className="mt-4 text-center">
                          <span className="text-white/80 text-xs font-bold uppercase tracking-widest">
                            {selectedImage.index} / {selectedImage.total} • {selectedImage.title}
                          </span>
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default ExpandableSolutions;
